// Inline SVG icons, ported from today's pages (components/Icons.tsx). State
// and priority icons follow Linear's shapes so the board reads the same way;
// colours come from CSS.
import type { SVGProps } from 'react'
import type { PlanStatus, Priority, StateSlug } from '../api/types'
import { planStatusName, priorityName, stateName } from '../vocab'

type IconProps = SVGProps<SVGSVGElement> & { size?: number }

function Svg({ size = 16, children, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false" {...rest}>
      {children}
    </svg>
  )
}

export function SearchIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="m10.5 10.5 3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </Svg>
  )
}

type Shape = 'dashed' | 'empty' | 'half' | 'three-quarters' | 'done' | 'cancelled'

function Progress({ kind }: { kind: Shape }) {
  switch (kind) {
    case 'dashed':
      return <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.6" strokeDasharray="2.2 2.2" />
    case 'empty':
      return <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.6" />
    case 'half':
      return (
        <>
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.6" />
          <path d="M8 4.5a3.5 3.5 0 0 1 0 7z" fill="currentColor" />
        </>
      )
    case 'three-quarters':
      return (
        <>
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.6" />
          <path d="M8 8V4.5a3.5 3.5 0 1 1-3.5 3.5z" fill="currentColor" />
        </>
      )
    case 'done':
      return (
        <>
          <circle cx="8" cy="8" r="7" fill="currentColor" />
          <path d="m5 8.2 2 2 4-4.2" stroke="var(--icon-on-fill)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )
    case 'cancelled':
      return (
        <>
          <circle cx="8" cy="8" r="7" fill="currentColor" />
          <path d="m5.6 5.6 4.8 4.8m0-4.8-4.8 4.8" stroke="var(--icon-on-fill)" strokeWidth="1.6" strokeLinecap="round" />
        </>
      )
  }
}

const STATE_SHAPE: Record<StateSlug, Shape> = {
  backlog: 'dashed',
  todo: 'empty',
  in_progress: 'half',
  on_main: 'three-quarters',
  done: 'done',
  cancelled: 'cancelled',
  duplicate: 'cancelled',
}

/** A state's icon; `labelled` names it for a screen reader, where no word beside it does (today's). */
export function StateIcon({ state, size = 14, labelled = false }: { state: StateSlug; size?: number; labelled?: boolean }) {
  return (
    <Svg size={size} className={`state-icon state-${state}`} aria-hidden={labelled ? undefined : true} role={labelled ? 'img' : undefined} aria-label={labelled ? stateName(state) : undefined}>
      <Progress kind={STATE_SHAPE[state]} />
    </Svg>
  )
}

const PLAN_SHAPE: Record<PlanStatus, Shape> = {
  backlog: 'dashed',
  planned: 'empty',
  in_progress: 'half',
  for_review: 'three-quarters',
  completed: 'done',
  cancelled: 'cancelled',
}

/** A plan's status icon, in the shapes of the states (today's). */
export function PlanStatusIcon({ status, size = 14, labelled = false }: { status: PlanStatus; size?: number; labelled?: boolean }) {
  return (
    <Svg size={size} className={`state-icon plan-${status}`} aria-hidden={labelled ? undefined : true} role={labelled ? 'img' : undefined} aria-label={labelled ? planStatusName(status) : undefined}>
      <Progress kind={PLAN_SHAPE[status]} />
    </Svg>
  )
}

export function LinkIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M9.5 2.5h4v4M13.5 2.5 8 8M12 9.5v3a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  )
}

/** How many of the three bars a priority fills. */
const BARS: Partial<Record<Priority, number>> = { high: 3, medium: 2, low: 1 }

export function PriorityIcon({ priority, size = 14 }: { priority: Priority; size?: number }) {
  const name = priorityName(priority)
  if (priority === 'urgent') {
    return (
      <Svg size={size} className="priority-icon priority-urgent" role="img" aria-hidden={undefined} aria-label={name}>
        <title>{name}</title>
        <rect x="1.5" y="1.5" width="13" height="13" rx="3.5" fill="currentColor" />
        <path d="M8 4.5v4.2" stroke="var(--icon-on-fill)" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="8" cy="11.3" r="1.05" fill="var(--icon-on-fill)" />
      </Svg>
    )
  }
  if (priority === 'none') {
    return (
      <Svg size={size} className="priority-icon priority-none" role="img" aria-hidden={undefined} aria-label={name}>
        <title>{name}</title>
        <rect x="2" y="7.25" width="2.6" height="1.5" rx=".6" fill="currentColor" />
        <rect x="6.7" y="7.25" width="2.6" height="1.5" rx=".6" fill="currentColor" />
        <rect x="11.4" y="7.25" width="2.6" height="1.5" rx=".6" fill="currentColor" />
      </Svg>
    )
  }
  const filled = BARS[priority] ?? 0
  return (
    <Svg size={size} className={`priority-icon priority-${priority}`} role="img" aria-hidden={undefined} aria-label={name}>
      <title>{name}</title>
      {[0, 1, 2].map((i) => (
        <rect key={i} x={2 + i * 4.6} y={10 - i * 3} width="2.8" height={4 + i * 3} rx=".8" fill="currentColor" opacity={i < filled ? 1 : 0.28} />
      ))}
    </Svg>
  )
}

export function DocIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 1.75h5.2L12.5 5v9.25H4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M9 2v3.25h3.25M6 8.5h4.5M6 11h4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </Svg>
  )
}

export function PlanIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="2" y="2.5" width="12" height="11" rx="2.2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 6h6M5 8.5h4M5 11h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </Svg>
  )
}

export function CheckIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  )
}

export function PaperclipIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M13 7.4 8 12.4a3.1 3.1 0 0 1-4.4-4.4l5.3-5.3a2 2 0 0 1 2.9 2.9L6.5 10.9a.9.9 0 0 1-1.3-1.3L10 4.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  )
}

export function CommentIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M2.5 4.25c0-1 .8-1.75 1.75-1.75h7.5c1 0 1.75.8 1.75 1.75v5c0 1-.8 1.75-1.75 1.75H7.5L4.5 13.5V11h-.25c-1 0-1.75-.8-1.75-1.75z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </Svg>
  )
}

/** A commit: a dot on its line. */
export function CommitIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="8" cy="8" r="2.6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M1.5 8h3.9M10.6 8h3.9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </Svg>
  )
}

/** Points right; turned down, by CSS, when what it opens is open. */
export function ChevronRightIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m6 3.5 4.5 4.5L6 12.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  )
}

/** The logo's mark: the head and four arms. */
export function BoardLogo({ size = 16, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden="true" focusable="false" {...rest}>
      <path fillRule="evenodd" d="M6 30A26 26 0 0 1 58 30ZM27 19A3 3 0 1 0 21 19A3 3 0 1 0 27 19ZM43 19A3 3 0 1 0 37 19A3 3 0 1 0 43 19Z" fill="var(--accent)" />
      <rect x="6" y="33" width="9" height="18" rx="4.5" fill="var(--logo-arm-1)" />
      <rect x="20.33" y="33" width="9" height="26" rx="4.5" fill="var(--logo-arm-2)" />
      <rect x="34.67" y="33" width="9" height="12" rx="4.5" fill="var(--logo-arm-3)" />
      <rect x="49" y="33" width="9" height="22" rx="4.5" fill="var(--logo-arm-4)" />
    </svg>
  )
}

/**
 * The logo's word, "poulpe" in Fredoka 600, as outlines, so that no font is
 * loaded for six letters.
 */
export function BoardWordmark({ height = 21, ...rest }: SVGProps<SVGSVGElement> & { height?: number }) {
  return (
    <svg width={(height * 295.2) / 120} height={height} viewBox="0 -10 295.2 120" fill="currentColor" aria-hidden="true" focusable="false" {...rest}>
      <path d="M26.7 86.9Q21.8 86.9 18.1 85Q14.3 83 11.9 79.7Q9.4 76.3 8.2 71.9Q6.9 67.5 7 62.4Q7 57.5 8.3 53Q9.6 48.6 12.1 45.2Q14.5 41.7 18.2 39.8Q21.8 37.8 26.7 37.8Q31.1 37.8 35.3 39.8Q39.4 41.8 42.6 45.2Q45.8 48.7 47.7 53.2Q49.5 57.7 49.5 62.7Q49.5 67.7 47.7 72.2Q45.8 76.6 42.6 79.9Q39.4 83.2 35.3 85.1Q31.2 86.9 26.7 86.9ZM7.4 108.5Q3.8 108.5 2.3 107.5Q0.7 106.4 0.4 104.8Q0 103.1 0 101.2V45.3Q0 43.3 0.3 41.7Q0.6 40 2.2 38.9Q3.7 37.8 7.3 37.8Q10.4 37.8 12.3 38.7Q14.1 39.6 14.9 41.5V101.1Q15 103 14.6 104.7Q14.2 106.4 12.7 107.5Q11.1 108.5 7.4 108.5ZM24.6 72.8Q27.4 72.8 29.6 71.5Q31.8 70.1 33.2 67.8Q34.5 65.5 34.5 62.7Q34.5 59.8 33.2 57.6Q31.9 55.3 29.7 54Q27.5 52.7 24.7 52.7Q22 52.7 19.8 54.1Q17.5 55.4 16.3 57.7Q15 59.9 15 62.7Q15 65.5 16.3 67.8Q17.6 70.1 19.8 71.5Q22 72.8 24.6 72.8ZM78.7 88.6Q74 88.6 69.4 86.8Q64.7 84.9 60.9 81.5Q57.1 78.1 54.9 73.4Q52.7 68.6 52.7 62.8Q52.7 57.4 54.7 52.8Q56.7 48.1 60.3 44.5Q63.9 40.9 68.6 38.9Q73.2 36.8 78.5 36.8Q85.7 36.8 91.6 40.2Q97.4 43.7 100.9 49.6Q104.4 55.5 104.4 62.7Q104.4 68.8 102.2 73.6Q99.9 78.3 96.2 81.7Q92.4 85.1 87.9 86.9Q83.3 88.6 78.7 88.6ZM78.5 73.5Q80.9 73.5 83.4 72.3Q85.9 71.1 87.6 68.7Q89.3 66.3 89.3 62.6Q89.3 59.4 87.9 57Q86.5 54.6 84.1 53.2Q81.6 51.9 78.4 51.9Q75.4 51.9 73.1 53.3Q70.7 54.7 69.3 57.2Q67.8 59.6 67.8 62.7Q67.8 66.3 69.5 68.8Q71.1 71.2 73.6 72.3Q76.1 73.5 78.5 73.5ZM131.1 87.7Q126.5 87.7 122.5 85.9Q118.4 84.1 115.4 80.9Q112.4 77.6 110.8 73.2Q109.1 68.7 109.1 63.4V45.3Q109.1 43.3 109.5 41.6Q109.9 39.9 111.5 38.8Q113 37.7 116.7 37.7Q120.4 37.7 122 38.8Q123.5 39.9 123.9 41.7Q124.2 43.4 124.2 45.4V63.4Q124.2 66.3 125.3 68.5Q126.3 70.6 128.4 71.7Q130.4 72.8 133.2 72.8Q136.1 72.8 138.2 71.7Q140.2 70.5 141.4 68.4Q142.5 66.3 142.5 63.4V45.2Q142.5 43.2 142.9 41.5Q143.3 39.8 144.9 38.8Q146.4 37.7 150.1 37.7Q153.8 37.7 155.4 38.8Q156.9 39.9 157.3 41.7Q157.6 43.4 157.6 45.3V80.5Q157.6 82.4 157.3 84.1Q156.9 85.7 155.3 86.7Q153.7 87.7 150.1 87.7Q147.4 87.7 145.9 87.1Q144.4 86.5 143.7 85.6Q143 84.7 142.9 83.7Q142.7 82.7 142.7 81.9L144 80.8Q143.7 81.2 142.7 82.3Q141.7 83.4 140.1 84.7Q138.4 85.9 136.2 86.8Q134 87.7 131.1 87.7ZM183.8 87.8Q179.7 87.8 176.6 87.4Q173.4 86.9 171.2 85.7Q168.9 84.5 167.5 82.4Q166.1 80.3 165.4 77Q164.8 73.6 164.8 68.7V22.5Q164.8 20.5 165.2 18.8Q165.5 17.1 167.1 16Q168.7 14.9 172.3 14.9Q175.9 14.9 177.4 16Q179 17.1 179.4 18.8Q179.8 20.5 179.8 22.5V68Q179.8 69.6 179.9 70.7Q180.1 71.7 180.6 72.3Q181 72.9 181.8 73.1Q182.6 73.3 183.9 73.3Q185.1 73.3 186.3 73.6Q187.6 73.9 188.5 75.4Q189.4 76.9 189.4 80.4Q189.4 84 188.5 85.6Q187.6 87.2 186.3 87.5Q185.1 87.8 183.8 87.8ZM219.9 86.9Q215 86.9 211.2 85Q207.5 83 205.1 79.7Q202.6 76.3 201.3 71.9Q200.1 67.5 200.2 62.4Q200.2 57.5 201.5 53Q202.8 48.6 205.2 45.2Q207.7 41.7 211.3 39.8Q215 37.8 219.9 37.8Q224.3 37.8 228.4 39.8Q232.6 41.8 235.8 45.2Q239 48.7 240.8 53.2Q242.7 57.7 242.7 62.7Q242.7 67.7 240.8 72.2Q239 76.6 235.8 79.9Q232.6 83.2 228.5 85.1Q224.4 86.9 219.9 86.9ZM200.6 108.5Q197 108.5 195.4 107.5Q193.9 106.4 193.6 104.8Q193.2 103.1 193.2 101.2V45.3Q193.2 43.3 193.5 41.7Q193.8 40 195.3 38.9Q196.9 37.8 200.5 37.8Q203.6 37.8 205.4 38.7Q207.3 39.6 208.1 41.5V101.1Q208.2 103 207.8 104.7Q207.4 106.4 205.8 107.5Q204.3 108.5 200.6 108.5ZM217.8 72.8Q220.6 72.8 222.8 71.5Q225 70.1 226.3 67.8Q227.7 65.5 227.7 62.7Q227.7 59.8 226.4 57.6Q225.1 55.3 222.9 54Q220.7 52.7 217.9 52.7Q215.2 52.7 212.9 54.1Q210.7 55.4 209.4 57.7Q208.2 59.9 208.2 62.7Q208.2 65.5 209.5 67.8Q210.8 70.1 213 71.5Q215.2 72.8 217.8 72.8ZM272.7 88.7Q265.7 88.7 260.6 86.4Q255.6 84.1 252.4 80.4Q249.2 76.6 247.7 72Q246.1 67.4 246.1 62.8Q246.1 55.3 249.5 49.5Q252.9 43.7 258.9 40.4Q264.9 37 272.7 37Q278.5 37 282.8 38.9Q287 40.7 289.8 43.7Q292.5 46.6 293.9 50Q295.2 53.4 295.2 56.5Q295.2 62 292.6 64.7Q289.9 67.3 285.8 67.3H262Q262.2 69.9 263.9 71.7Q265.5 73.5 268 74.5Q270.5 75.5 273.3 75.5Q275.7 75.5 277.5 75.2Q279.3 75 280.6 74.6Q281.9 74.1 283 73.7Q284.1 73.2 285 72.9Q285.8 72.6 286.6 72.5Q288.1 72.4 289.4 73.4Q290.6 74.3 291.6 76.2Q292.3 77.3 292.6 78.3Q292.8 79.3 292.8 80.3Q292.8 82.7 290.4 84.6Q287.9 86.5 283.5 87.6Q279 88.7 272.7 88.7ZM262 59.1H277.8Q279.4 59.1 280.2 58.6Q280.9 58 280.9 56.4Q280.9 54.6 279.8 53.2Q278.7 51.9 276.9 51.2Q275 50.4 272.6 50.4Q269.5 50.4 267.1 51.6Q264.7 52.8 263.4 54.8Q262 56.8 262 59.1Z" />
    </svg>
  )
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m4.5 6.5 3.5 3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  )
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m9.5 4.5-3.5 3.5 3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  )
}

export function CloseIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m4 4 8 8m0-8-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </Svg>
  )
}

export function UserIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="8" cy="5.5" r="2.75" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2.75 14c.6-2.6 2.7-4.25 5.25-4.25S12.65 11.4 13.25 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </Svg>
  )
}

export function PlusIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </Svg>
  )
}


/** Two branches joining: a merge. */
export function MergeIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="4.5" cy="3.5" r="1.75" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="4.5" cy="12.5" r="1.75" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="11.5" cy="8" r="1.75" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4.5 5.25v5.5M4.5 5.5c0 2 2.5 2.5 5.25 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </Svg>
  )
}
